import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import CourseCard from '../components/CourseCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, PlayCircle, Users, BookOpen, Award, Sparkles, Mail, Loader2, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [courseOfWeek, setCourseOfWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const [coursesRes, cowRes] = await Promise.all([
          axios.get(`${API}/courses?published_only=true`),
          axios.get(`${API}/course-of-week`).catch(() => ({ data: { course: null } }))
        ]);
        setFeaturedCourses(coursesRes.data.slice(0, 4));
        setCourseOfWeek(cowRes.data.course);
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, frontend_url: window.location.origin });
      toast.success('Check your email to confirm your subscription!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const stats = [
    { icon: Users, label: 'Active Learners', value: '10,000+' },
    { icon: BookOpen, label: 'Expert Courses', value: '500+' },
    { icon: Award, label: 'Completion Rate', value: '94%' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-indigo-50 via-white to-lime-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-slate-700">AI-Powered Learning Platform</span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-none text-slate-900">
                AISkillFlow
                <br />
                <span className="text-primary">Your Flow to AI Skills & Income</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-lg leading-relaxed">
                Master AI tools, build real skills, and unlock new income streams. 
                Access 18+ expert courses, an AI learning coach, and hands-on projects — all in one membership.
              </p>
              <p className="text-sm text-slate-400 font-medium">
                Powered by <a href="https://aiskillflowpro.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">AISkillFlowPro</a>
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 h-12 px-8 shadow-lg shadow-indigo-500/20"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta"
                >
                  Start Learning Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="h-12 px-8"
                  onClick={() => navigate('/courses')}
                  data-testid="hero-browse"
                >
                  <PlayCircle className="w-5 h-5 mr-2" />
                  Browse Courses
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -top-8 -left-8 w-72 h-72 bg-secondary/30 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -right-8 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
              <img 
                src="https://images.unsplash.com/photo-1758872014929-174e4ccdf01f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Student learning"
                className="relative rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="flex items-center gap-4 justify-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-slate-600">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course of the Week */}
      {courseOfWeek && (
        <section className="py-12 md:py-16 bg-gradient-to-r from-amber-50 via-white to-amber-50 border-b border-amber-100" data-testid="course-of-week-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-xl">
                <Trophy className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-slate-900">Course of the Week</h2>
                <p className="text-sm text-slate-500">Our top pick for this week</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-8 items-center bg-white rounded-2xl border border-amber-200 p-6 md:p-8 shadow-sm">
              <div className="w-full md:w-2/5 aspect-video rounded-xl overflow-hidden">
                <img 
                  src={courseOfWeek.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'} 
                  alt={courseOfWeek.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">PICK OF THE WEEK</span>
                  <span className="text-sm text-slate-500">{courseOfWeek.category}</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">{courseOfWeek.title}</h3>
                <p className="text-slate-600 mb-4 line-clamp-2">{courseOfWeek.description}</p>
                <div className="flex items-center gap-4 mb-6 text-sm text-slate-500">
                  <span>{courseOfWeek.lessons?.length || courseOfWeek.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0} lessons</span>
                  <span>{courseOfWeek.level || courseOfWeek.difficulty || 'All levels'}</span>
                  {courseOfWeek._source === 'academy' ? (
                    <span className="font-bold text-indigo-600">AI Academy</span>
                  ) : (courseOfWeek.is_free || courseOfWeek.price === 0) ? (
                    <span className="font-bold text-emerald-600">FREE</span>
                  ) : courseOfWeek.price ? (
                    <span className="font-bold text-slate-900">${courseOfWeek.price}</span>
                  ) : null}
                </div>
                <Button 
                  onClick={() => navigate(courseOfWeek._source === 'academy' ? `/academy/courses/${courseOfWeek.slug}` : `/courses/${courseOfWeek.id}`)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="cow-enroll-btn"
                >
                  View Course <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Courses */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                Featured Courses
              </h2>
              <p className="mt-2 text-slate-600">Start your learning journey with our top-rated courses</p>
            </div>
            <Link 
              to="/courses" 
              className="hidden sm:flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
              data-testid="view-all-courses"
            >
              View all courses
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-slate-200 aspect-video rounded-t-xl" />
                  <div className="p-5 space-y-3 bg-white rounded-b-xl border border-t-0 border-slate-200">
                    <div className="h-4 bg-slate-200 rounded w-1/3" />
                    <div className="h-6 bg-slate-200 rounded" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredCourses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No courses yet</h3>
              <p className="text-slate-600 mb-6">Be the first to create and publish a course!</p>
              <Button onClick={() => navigate('/register')} data-testid="become-instructor">
                Become an Instructor
              </Button>
            </div>
          )}

          <div className="sm:hidden mt-8 text-center">
            <Link 
              to="/courses" 
              className="inline-flex items-center gap-2 text-primary font-medium"
            >
              View all courses
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Ready to start learning?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Join AISkillFlow today and start building AI skills that earn.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 px-8"
              onClick={() => navigate('/courses')}
              data-testid="cta-pricing"
            >
              Browse Courses
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10 h-12 px-8"
              onClick={() => navigate('/register')}
              data-testid="cta-signup"
            >
              Sign Up Free
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 to-purple-50">
        <div className="max-w-xl mx-auto px-4 text-center">
          <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
            Stay in the loop
          </h2>
          <p className="text-slate-600 mb-6">
            Get updates on new courses, tips, and exclusive offers.
          </p>
          <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12"
              data-testid="newsletter-email"
            />
            <Button type="submit" className="h-12 px-6" disabled={subscribing} data-testid="newsletter-submit">
              {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-xl">AISkillFlow</span>
              </div>
              <p className="text-slate-400 text-sm">
                Your flow to AI skills and income. Master AI tools and grow your business.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/courses" className="hover:text-white transition-colors">Browse Courses</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/instructor/pricing" className="hover:text-white transition-colors">Teach on AISkillFlow</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/knowledge-base" className="hover:text-white transition-colors">Knowledge Base</Link></li>
                <li><a href="mailto:info@aiskillflowpro.com" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-center text-slate-400 text-sm">
            © {new Date().getFullYear()} AISkillFlow. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
